import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "./Top3";
import { ExternalLink, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import EditableText from "./EditableText";
import SourceBadge from "./SourceBadge";
import { useReorder } from "../lib/useReorder";
import { useEditMode } from "../lib/EditModeContext";

const WaitingOn = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const { editMode } = useEditMode();

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.waitingTodoist();
      setRows(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const { rowProps, handleProps, dragId, overId } = useReorder(rows, async (newIds) => {
    setRows((prev) => newIds.map((id) => prev.find((p) => p.id === id)).filter(Boolean));
    try { await api.reorderWaitingTodoist(newIds); } catch { load(); }
  });

  const editRow = async (r, newContent) => {
    setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, content: newContent } : x));
    try { await api.updateTodoistTask(r.id, newContent); toast("Updated in Todoist"); }
    catch { toast("Todoist update failed"); load(); }
  };

  const removeRow = async (r) => {
    setRows((prev) => prev.filter((x) => x.id !== r.id));
    try {
      await api.closeTodoistTask(r.id);
      toast("Closed in Todoist", {
        action: {
          label: "UNDO",
          onClick: async () => {
            try { await api.reopenTodoistTask(r.id); toast("Reopened"); load(); }
            catch { toast("Undo failed"); }
          },
        },
        duration: 6000,
      });
    } catch { toast("Todoist close failed"); load(); }
  };

  return (
    <section data-testid="waiting-on-section">
      <SectionHeader
        code="03" label="WAITING ON"
        caption="Live from Todoist · @Waiting label"
        labelKey="section.waiting.label"
        captionKey="section.waiting.caption"
      />
      <div className="op-panel">
        {loading && rows.length === 0 && (
          <div className="p-6 text-center text-[#6A7396] op-mono text-[11px] tracking-[0.24em]">
            LOADING…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="p-6 text-center text-[#6A7396]" data-testid="waiting-empty">
            <div className="text-sm">
              No <span className="text-[#4A9EFF]">@Waiting</span> tasks in Todoist.
            </div>
          </div>
        )}
        {rows.length > 0 && (
          <ul className="divide-y divide-white/[0.04]">
            {rows.map((r) => (
              <li
                key={r.id}
                data-testid={`waiting-${r.id}`}
                {...rowProps(r.id)}
                data-dragging={dragId === r.id}
                data-drop-active={overId === r.id}
                className="op-editable-row flex items-center gap-3 px-6 py-4 group"
              >
                <span className="op-tag text-[#B4BAD1] w-40 shrink-0 truncate hidden md:inline">
                  {r.business || "—"}
                </span>
                <EditableText
                  value={r.content}
                  onSave={(v) => editRow(r, v)}
                  className="text-white text-sm flex-1"
                  multiline
                  testid={`waiting-content-${r.id}`}
                />
                <SourceBadge source="todoist" />
                {r.todoist_url && (
                  <a
                    href={r.todoist_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#6BC5FF]/70 hover:text-[#6BC5FF]"
                    title="Open in Todoist"
                  >
                    <ExternalLink size={12} />
                  </a>
                )}
                <div className="flex items-center gap-1.5">
                  {editMode && (
                    <span
                      {...handleProps(r.id)}
                      className="op-row-handle"
                      data-testid={`waiting-drag-${r.id}`}
                      aria-label="Drag to reorder"
                    >
                      <GripVertical size={12} strokeWidth={1.6} />
                    </span>
                  )}
                  <button
                    onClick={() => removeRow(r)}
                    className="op-row-delete opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`waiting-delete-${r.id}`}
                    aria-label="Close in Todoist"
                    title="Close in Todoist"
                    style={{ opacity: editMode ? 1 : undefined }}
                  >
                    <X size={12} strokeWidth={2} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default WaitingOn;
